import { useState, useEffect, useCallback } from 'react';
import userService from '../services/userService';
import { processUsersWithPermissions } from '../utils/userUtils';
import { UX_TIMING, OPERATION_STATUS } from '../constants';

/**
 * Hook personalizado para gestión completa de usuarios
 * @returns {Object} - Estado y funciones para gestionar usuarios
 */
export const useUsers = () => {
  // Estados principales
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total: 0, admins: 0, collaborators: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados de paginación - NUEVO
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
    hasNext: false,
    hasPrev: false
  });

  // Estados de operaciones
  const [operationStatus, setOperationStatus] = useState(OPERATION_STATUS.IDLE);
  const [operationError, setOperationError] = useState(null);
  const [operatingUserName, setOperatingUserName] = useState('');
  const [isEditingMode, setIsEditingMode] = useState(false);

  /**
   * Carga usuarios y estadísticas desde el backend con paginación
   */
  const loadUsers = useCallback(async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      setError(null);
      
      const [usersResponse, statsResponse] = await Promise.all([
        userService.getAllUsers({ page, limit }),
        userService.getUserStats()
      ]);
      
      if (usersResponse.success) {
        // Los usuarios están en data.users en la nueva estructura del backend
        const usersData = usersResponse.data?.users || usersResponse.data || [];
        const processedUsers = processUsersWithPermissions(usersData);
        setUsers(processedUsers);
        
        // Capturar información de paginación del backend
        if (usersResponse.data?.pagination) {
          setPagination(usersResponse.data.pagination);
        }
      }
      
      if (statsResponse.success) {
        // Las estadísticas pueden estar directamente en data o en data.stats
        const statsData = statsResponse.data?.stats || statsResponse.data || { total: 0, admins: 0, collaborators: 0 };
        setStats(statsData);
      }
      
    } catch (err) {
      console.error('Error cargando usuarios:', err);
      setError(err.message || 'Error cargando los datos de usuarios');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cambiar a una página específica
   */
  const goToPage = useCallback(async (page) => {
    await loadUsers(page, pagination.limit);
  }, [loadUsers, pagination.limit]);

  /**
   * Ir a la página siguiente
   */
  const nextPage = useCallback(async () => {
    if (pagination.hasNext) {
      await goToPage(pagination.page + 1);
    }
  }, [pagination.hasNext, pagination.page, goToPage]);

  /**
   * Ir a la página anterior
   */
  const prevPage = useCallback(async () => {
    if (pagination.hasPrev) {
      await goToPage(pagination.page - 1);
    }
  }, [pagination.hasPrev, pagination.page, goToPage]);

  /**
   * Cambiar el número de elementos por página
   */
  const changePageSize = useCallback(async (newLimit) => {
    await loadUsers(1, newLimit); // Reiniciar a página 1 con nuevo tamaño
  }, [loadUsers]);

  /**
   * Ejecuta una operación de usuario con UX optimizada
   */
  const executeUserOperation = useCallback(async (operation, userData, isEdit = false) => {
    setOperatingUserName(userData.nombre);
    setOperationStatus(OPERATION_STATUS.LOADING);
    setIsEditingMode(isEdit);
    setOperationError(null);

    const startTime = Date.now();

    try {
      const response = await operation();

      // Asegurar tiempo mínimo de loading para UX
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, UX_TIMING.LOADING_MIN_TIME - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }

      if (response.success) {
        setOperationStatus(OPERATION_STATUS.SUCCESS);
        await loadUsers(); // Recargar lista
        return { success: true };
      } else {
        setOperationStatus(OPERATION_STATUS.ERROR);
        setOperationError(response.message || 'Ocurrió un error desconocido.');
        return { success: false, message: response.message };
      }

    } catch (error) {
      console.error('Error en operación de usuario:', error);
      setOperationStatus(OPERATION_STATUS.ERROR);
      setOperationError(error.message || 'Ocurrió un error de conexión.');
      return { success: false, message: error.message };
    }
  }, [loadUsers]);

  /**
   * Crea un nuevo usuario
   */
  const createUser = useCallback(async (userData) => {
    return executeUserOperation(
      () => userService.createUser(userData),
      userData,
      false
    );
  }, [executeUserOperation]);

  /**
   * Actualiza un usuario existente
   */
  const updateUser = useCallback(async (userId, userData) => {
    return executeUserOperation(
      () => userService.updateUser(userId, { ...userData, id: userId }),
      userData,
      true
    );
  }, [executeUserOperation]);

  /**
   * Elimina un usuario
   */
  const deleteUser = useCallback(async (userId, userData = { nombre: 'Usuario' }) => {
    return executeUserOperation(
      () => userService.deleteUser(userId),
      userData,
      false
    );
  }, [executeUserOperation]);

  /**
   * Resetea el estado de operación
   */
  const resetOperationState = useCallback(() => {
    setOperationStatus(OPERATION_STATUS.IDLE);
    setOperationError(null);
    setOperatingUserName('');
    setIsEditingMode(false);
  }, []);

  // Cargar datos al montar el componente
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  return {
    // Estados
    users,
    stats,
    loading,
    error,
    operationStatus,
    operationError,
    operatingUserName,
    isEditingMode,
    pagination, // Exponer el estado de paginación
    
    // Funciones
    loadUsers,
    createUser,
    updateUser,
    deleteUser,
    resetOperationState,
    goToPage, // Exponer funciones de paginación
    nextPage,
    prevPage,
    changePageSize,
  };
}; 